import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import { getTicketById, updateTicket } from '../services/TicketDeCaisseService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import { searchCustomers } from '../../customers/services/customerService';
import { getVatRates } from '../../companysettings/services/vatService';
import LineItemsEditor from '../components/LineItemsEditor';
import { PAYMENT_STATUS, getPaymentStatus } from '../../../constants/paymentStatus';
import TICKET_TYPE, { getTicketTypeLabel, getTicketTypeColor } from '../../../constants/ticketTypes';

const TicketEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const debounceRef = useRef(null);

  // Get payment methods from Redux store
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);

  // Form fields
  const [selectedCustomer, setSelectedCustomer] = useState(null); // ✅ New: Customer object from search
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [ticketType, setTicketType] = useState(TICKET_TYPE.SALE);
  const [pickupDate, setPickupDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [vatPercentage, setVatPercentage] = useState(21);
  const [vatRates, setVatRates] = useState([]); // ✅ Available VAT rates from API
  const [lineItems, setLineItems] = useState([]);


  useEffect(() => {
    fetchTicketDetails();
    
    // Fetch payment methods if not already loaded
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }

    // Fetch VAT rates
    const fetchVatRatesData = async () => {
      try {
        const response = await getVatRates();
        const rates = response?.payload || response || [];
        setVatRates(rates);
      } catch (error) {

      }
    };
    fetchVatRatesData();
  }, [id, dispatch]);

  // ✅ Update payment method when store loads - ensure type consistency
  useEffect(() => {
    if (ticket && paymentMethodsFromStore.length > 0 && !paymentMethodId) {
      let matchingPaymentMethod = null;
      
      // First, try to match by PaymentMethodId
      if (ticket.PaymentMethodId) {
        matchingPaymentMethod = paymentMethodsFromStore.find(
          pm => Number(pm.id) === Number(ticket.PaymentMethodId)
        );
      }
      
      // If not found by ID, try to match by PaymentMethodName
      if (!matchingPaymentMethod && ticket.PaymentMethodName) {
        matchingPaymentMethod = paymentMethodsFromStore.find(
          pm => pm.name?.toUpperCase() === ticket.PaymentMethodName?.toUpperCase()
        );
      }
      
      // If still not found, try PaymentMethod field as fallback
      if (!matchingPaymentMethod && ticket.PaymentMethod) {
        matchingPaymentMethod = paymentMethodsFromStore.find(
          pm => pm.name?.toUpperCase() === ticket.PaymentMethod?.toUpperCase()
        );
      }
      
      if (matchingPaymentMethod) {
        setPaymentMethodId(Number(matchingPaymentMethod.id));
        setPaymentMethod(matchingPaymentMethod.name?.toUpperCase() || matchingPaymentMethod.name);
      }
    }
  }, [paymentMethodsFromStore, ticket, paymentMethodId]);

  // Customer search function with debounce
  const loadCustomerOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.trim().length < 2) {
        return callback([]);
      }

      try {
        const customers = await searchCustomers(inputValue);
        const customersArray = Array.isArray(customers) ? customers : [];
        callback(customersArray);
      } catch (error) {

        callback([]);
      }
    }, 400);
  };

  // ✅ Handle customer selection
  const handleCustomerChange = (customer) => {
    if (customer) {
      setSelectedCustomer(customer);
      setCustomerName(customer.Name || customer.CompanyName || '');
    } else {
      setSelectedCustomer(null);
    }
  };

  // ✅ Clear customer selection
  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
  };

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTicketById(id);

      // Handle different response structures
      let ticketData = null;
      if (response?.payload) {
        ticketData = response.payload;
      } else if (response?.data?.payload) {
        ticketData = response.data.payload;
      } else if (response?.data) {
        ticketData = response.data;
      } else {
        ticketData = response;
      }

      setTicket(ticketData);

      // ✅ FIX: Use only one source for line items - prefer LineItems over Items
      const items = ticketData.LineItems || ticketData.Items || [];
      setLineItems(items);

      // ✅ Handle customer - can be string or object
      if (ticketData.CustomerId && (ticketData.CustomerName || ticketData.Name)) {
        // If we have CustomerId, it's an existing customer - create customer object
        const customerObj = {
          Id: ticketData.CustomerId,
          Name: ticketData.CustomerName || ticketData.Name,
          CompanyName: ticketData.CompanyName,
          Email: ticketData.CustomerEmail,
          Phone: ticketData.CustomerPhone,
          value: ticketData.CustomerId,
          label: ticketData.CustomerName || ticketData.Name,
        };
        setSelectedCustomer(customerObj);
        setCustomerName(ticketData.CustomerName || ticketData.Name || '');
      } else if (ticketData.CustomerName) {
        // Just a string customer name (walk-in)
        setCustomerName(ticketData.CustomerName);
        setSelectedCustomer(null);
      } else {
        setCustomerName('');
        setSelectedCustomer(null);
      }

      setCustomerPhone(ticketData.CustomerPhone || '');
      setTicketType(ticketData.TicketType || TICKET_TYPE.SALE);
      setPickupDate(ticketData.PickupDate ? new Date(ticketData.PickupDate).toISOString().slice(0, 10) : '');
      
      // ✅ Payment method will be set by the useEffect that matches against paymentMethodsFromStore
      // Just store the payment method name for now
      if (ticketData.PaymentMethodName || ticketData.PaymentMethod) {
        setPaymentMethod(ticketData.PaymentMethodName?.trim().toUpperCase() || ticketData.PaymentMethod?.trim().toUpperCase() || '');
      }
      
      setPaidAmount(ticketData.PaidAmount?.toString() || '');
      setNotes(ticketData.Notes || '');
      
      // ✅ Get VAT from ticket data (from API response)
      setVatPercentage(ticketData.VATPercentage || 21);

      if (ticketData.DiscountAmount && ticketData.DiscountAmount > 0) {
        setDiscountType(ticketData.DiscountType || 'Fixed');
        setDiscountValue(
          ticketData.DiscountType?.toLowerCase() === 'percentage'
            ? ticketData.DiscountPercentage?.toString() || ''
            : ticketData.DiscountAmount?.toString() || ''
        );
        setDiscountReason(ticketData.DiscountReason || '');
      }
    } catch (error) {

      setError(error.message || 'Failed to load ticket details');
      toast.error(t('failed_to_load_ticket') || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/tickets/view/${id}`);
  };

  const handleSave = async () => {
    try {
      
      setSaving(true);

      // ✅ Validate line items
      if (!lineItems || lineItems.length === 0) {
        toast.error(t('at_least_one_item_required') || 'At least one item is required');
        setSaving(false);
        return;
      }


      // Get base subtotal from line items (sum of line items before VAT and discount)
      const subtotal = lineItems.reduce((sum, item) => {
        const itemSubtotal = (item.Quantity || 1) * (item.UnitPriceExclTax || item.UnitPrice || 0);
        return sum + itemSubtotal;
      }, 0);

      // Calculate discount details
      const vat = parseFloat(vatPercentage) || 21;
      let discAmount = 0;
      let discPercentage = null;
      let discType = null;
      let discReason = null;

      if (discountType && discountValue) {
        discType = discountType;
        discReason = discountReason.trim() || null;
        
        if (discountType.toLowerCase() === 'percentage') {
          discPercentage = parseFloat(discountValue) || 0;
          discAmount = (subtotal * discPercentage) / 100;
        } else {
          discAmount = parseFloat(discountValue) || 0;
        }
      }

      // Calculate totals
      const subtotalAfterDiscount = subtotal - discAmount;
      const vatAmount = (subtotalAfterDiscount * vat) / 100;
      
      // ✅ Calculate totalAmountInclVAT using UnitPriceInclVat to preserve original pricing
      const totalAmountInclVAT = lineItems.reduce((sum, item) => {
        const unitPrice = item.UnitPriceInclVat || (item.UnitPriceExclTax * (1 + vat / 100));
        return sum + (item.Quantity || 1) * unitPrice;
      }, 0);
      
      const totalAmountInclVATAfterDiscount = Math.round((totalAmountInclVAT - discAmount) * 100) / 100;

      // Calculate change (for UI reference only, not sent to backend)
      const paid = parseFloat(paidAmount) || 0;

      // Recalculate line items with new VAT and discount
      const updatedLineItems = lineItems.map(item => {
        const quantity = parseFloat(item.Quantity) || 1;
        const unitPriceExclTax = parseFloat(item.UnitPriceExclTax || item.UnitPrice) || 0;
        const unitPriceInclVat = unitPriceExclTax * (1 + vat / 100);

        // Calculate item discount
        let itemDiscountPercentage = 0;
        let itemDiscountAmount = 0;
        if (discType && discType.toLowerCase() === 'percentage' && discPercentage) {
          itemDiscountPercentage = discPercentage;
        }

        // Calculate line item totals
        const lineSubtotal = quantity * unitPriceExclTax;
        const lineDiscountAmount = (lineSubtotal * itemDiscountPercentage) / 100;
        const lineSubtotalAfterDiscount = lineSubtotal - lineDiscountAmount;
        const lineVATAmount = (lineSubtotalAfterDiscount * vat) / 100;
        const lineTotalInclVAT = lineSubtotal + (lineSubtotal * vat / 100); // Before discount
        const lineTotalInclVATAfterDiscount = lineSubtotalAfterDiscount + lineVATAmount;

        // ✅ Build line item DTO with proper validation
        const lineItemDto = {
          Id: item.Id || item.LineItemId || null, // ✅ Include existing line item ID
          ProductId: item.ProductId || null, // ✅ Always include ProductId (null if not existing product)
          ServiceName: item.ServiceName || null,
          ServiceDescription: item.ServiceDescription || null,
          ProductSerial: item.ProductSerial || null,
          Quantity: quantity,
          UnitPriceExclTax: Math.round(unitPriceExclTax * 100) / 100,
          UnitPriceInclVat: Math.round(unitPriceInclVat * 100) / 100,
          Discount: itemDiscountPercentage,
          DiscountAmount: Math.round(lineDiscountAmount * 100) / 100,
          VATAmount: Math.round(lineVATAmount * 100) / 100,
          Subtotal: Math.round(lineSubtotal * 100) / 100,
          TotalAmountInclVATAfterDiscount: Math.round(lineTotalInclVATAfterDiscount * 100) / 100,
          TotalAmountInclVAT: Math.round(lineTotalInclVAT * 100) / 100,
          UpdatePrice: item.UpdatePrice || false, // ✅ Include UpdatePrice flag
          SalePrice: Math.round((item.SalePrice || item.UnitPrice || unitPriceExclTax) * 100) / 100,
          VATPercentage: vat,
        };

        return lineItemDto;
      });

      // ✅ Validation: Ensure payment method ID is set
      if (!paymentMethodId) {
        toast.error('Please select a payment method');
        return;
      }

      // ✅ Validation: Line items must exist
      if (!lineItems || lineItems.length === 0) {
        toast.error('Cannot save ticket without line items');
        return;
      }

      // ✅ Calculate payment status based on paid amount and total
      const paymentStatus = getPaymentStatus(paid, totalAmountInclVATAfterDiscount);
      
      // ✅ Calculate change amount
      const changeAmount = paid > totalAmountInclVATAfterDiscount ? paid - totalAmountInclVATAfterDiscount : 0;

      // Prepare complete UpdateTicketDto (similar to CreateTicketDto)
      const updateData = {
        Id: parseInt(id), // ✅ Include ticket ID
        TicketType: ticketType, // ✅ Include ticket type
        CustomerPhone: customerPhone.trim() || null, // ✅ Include customer phone
        PickupDate: ticketType === TICKET_TYPE.REPAIR && pickupDate ? new Date(pickupDate).toISOString() : null, // ✅ Include pickup date for repair tickets
        CustomerId: selectedCustomer?.Id || ticket.CustomerId || null, // ✅ Use selected customer ID if available
        CustomerName: customerName.trim() || 'Walk-in Customer',
        PaymentMethodId: paymentMethodId, // ✅ Required field
        PaymentMethod: paymentMethod?.trim().toUpperCase() || null, // ✅ Deprecated field (for backward compatibility)
        Subtotal: Math.round(subtotal * 100) / 100,
        VATPercentage: vat,
        VATAmount: Math.round(vatAmount * 100) / 100,
        DiscountType: discType,
        DiscountPercentage: discPercentage,
        DiscountAmount: Math.round(discAmount * 100) / 100,
        DiscountReason: discReason,
        TotalAmountInclVATAfterDiscount: Math.round(totalAmountInclVATAfterDiscount * 100) / 100,
        TotalAmountInclVAT: Math.round(totalAmountInclVAT * 100) / 100,
        PaidAmount: paid > 0 ? Math.round(paid * 100) / 100 : null,
        AmountInChange: Math.round(changeAmount * 100) / 100, // ✅ Include change amount
        Notes: notes.trim() || '',
        LineItems: updatedLineItems,
        Status: paymentStatus, // ✅ Calculate status: Paid, Not Paid, or Partial Paid
      };

      
      // Verify PaymentMethodId is actually set
      if (!updateData.PaymentMethodId) {

        toast.error('Payment method is required');
        return;
      }

      await updateTicket(id, updateData);

      toast.success(t('ticket_updated_success') || 'Ticket updated successfully');
      navigate(`/tickets/view/${id}`);
    } catch (error) {

      toast.error(error.response?.data?.message || t('failed_to_update_ticket') || 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // ✅ Calculate live preview totals (preserve original UnitPriceInclVat to avoid rounding issues)
  const calculateLiveTotals = () => {
    // Use UnitPriceExclTax for base subtotal
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = (item.Quantity || 1) * (item.UnitPriceExclTax || item.UnitPrice || 0);
      return sum + itemSubtotal;
    }, 0);

    const vat = parseFloat(vatPercentage) || 21;
    let discAmount = 0;

    if (discountType && discountValue) {
      if (discountType.toLowerCase() === 'percentage') {
        const discPercentage = parseFloat(discountValue) || 0;
        discAmount = (subtotal * discPercentage) / 100;
      } else {
        discAmount = parseFloat(discountValue) || 0;
      }
    }

    const subtotalAfterDiscount = subtotal - discAmount;
    const vatAmount = (subtotalAfterDiscount * vat) / 100;
    
    // ✅ Use UnitPriceInclVat when available to preserve original pricing
    const totalInclBeforeDiscount = lineItems.reduce((sum, item) => {
      const unitPrice = item.UnitPriceInclVat || (item.UnitPriceExclTax * (1 + vat / 100));
      return sum + (item.Quantity || 1) * unitPrice;
    }, 0);
    
    const totalAmountInclVATAfterDiscount = Math.round((totalInclBeforeDiscount - discAmount) * 100) / 100;

    return {
      subtotal,
      discAmount,
      vatAmount,
      totalAmountInclVATAfterDiscount,
    };
  };

  const liveTotals = calculateLiveTotals();

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !ticket) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || t('ticket_not_found') || 'Ticket not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tickets')}
          sx={{ mt: 2 }}
        >
          {t('back_to_tickets') || 'Back to Tickets'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ bgcolor: '#f5f5f5' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {t('edit_ticket') || 'Edit Ticket'}
                </Typography>
                <Chip
                  label={getTicketTypeLabel(ticketType, t)}
                  color={getTicketTypeColor(ticketType)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {ticket.TicketNumber}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saving}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('saving') || 'Saving...' : t('save_changes') || 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* SECTION 1: Customer Information & Ticket Type */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 2,
          p: 2,
          bgcolor: ticketType === TICKET_TYPE.REPAIR ? '#fff3e0' : '#e3f2fd',
          borderRadius: 2,
          border: `2px solid ${ticketType === TICKET_TYPE.REPAIR ? '#ffcc80' : '#90caf9'}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: ticketType === TICKET_TYPE.REPAIR ? 'warning.main' : 'primary.main' }}>
            {ticketType === TICKET_TYPE.REPAIR ? '🔧 ' : '🛒 '}
            {ticketType === TICKET_TYPE.REPAIR ? (t('repair_information') || 'Repair Information') : (t('customer_information') || 'Customer Information')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 1 }}>
          {ticketType === TICKET_TYPE.REPAIR 
            ? (t('repair_information_description') || 'Manage customer and repair details')
            : (t('customer_information_description') || 'Manage customer details for this sale')
          }
        </Typography>

        <Grid container spacing={3}>
          {/* Ticket Type - Full Width First */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('ticket_type') || 'Ticket Type'}</InputLabel>
              <Select
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
                label={t('ticket_type') || 'Ticket Type'}
              >
                <MenuItem value={TICKET_TYPE.SALE}>
                  {getTicketTypeLabel(TICKET_TYPE.SALE, t)}
                </MenuItem>
                <MenuItem value={TICKET_TYPE.REPAIR}>
                  {getTicketTypeLabel(TICKET_TYPE.REPAIR, t)}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Customer Search or Name Input */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                {t('search_customer') || 'Search Customer'}
              </Typography>
              <AsyncSelect
                cacheOptions
                loadOptions={loadCustomerOptions}
                onChange={handleCustomerChange}
                value={selectedCustomer}
                placeholder={t('search_for_customer') || 'Search for a customer by name, email, or phone'}
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '56px',
                    borderColor: '#c4c4c4',
                    '&:hover': { borderColor: '#1976d2' },
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
                noOptionsMessage={() => t('no_customer_found') || 'No customers found'}
              />
              {!selectedCustomer && (
                <TextField
                  fullWidth
                  label={t('or_enter_new_customer') || 'Or enter new customer name'}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t('walk_in_customer') || 'Walk-in Customer'}
                  sx={{ mt: 2 }}
                  helperText={t('customer_name_helper') || 'Leave empty for walk-in customer'}
                />
              )}
              {selectedCustomer && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`Selected: ${selectedCustomer.Name || selectedCustomer.CompanyName}`}
                    onDelete={handleClearCustomer}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* Customer Phone (only for Repair Tickets) */}
          {ticketType === TICKET_TYPE.REPAIR && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customer_phone') || 'Customer Phone'}
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder={t('enter_customer_phone') || 'Enter phone number'}
                type="tel"
                helperText={t('customer_phone_helper') || 'Contact number for pickup notification'}
              />
            </Grid>
          )}

          {/* Pickup Date (only for Repair Tickets) */}
          {ticketType === TICKET_TYPE.REPAIR && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('pickup_date') || 'Pickup Date'}
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                type="date"
                InputLabelProps={{ shrink: true }}
                helperText={t('pickup_date_helper') || 'Expected date for item pickup'}
                required
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* SECTION 2: Line Items */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 2,
          p: 2,
          bgcolor: '#f3e5f5',
          borderRadius: 2,
          border: '2px solid #ce93d8'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>
            🛍️ {t('line_items') || 'Line Items'}
          </Typography>
        </Box>
        <LineItemsEditor
          lineItems={lineItems}
          onLineItemsChange={setLineItems}
          vatPercentage={parseFloat(vatPercentage) || 21}
        />
      </Paper>

      {/* SECTION 3: Payment, Discount & Notes - All in ONE Container */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* 3.1 - Payment Information */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            p: 2,
            bgcolor: '#e8f5e9',
            borderRadius: 2,
            border: '2px solid #c8e6c9'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              💳 {t('payment_information') || 'Payment Information'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 1 }}>
            {t('payment_information_description') || 'Manage payment details and pricing'}
          </Typography>

          <Grid container spacing={3}>
            {/* Payment Method */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                <Select
                  value={paymentMethodId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedPm = paymentMethodsFromStore.find(pm => pm.id === selectedId);
                    setPaymentMethodId(selectedId);
                    setPaymentMethod(selectedPm?.name?.toUpperCase() || selectedPm?.name || '');
                  }}
                  label={t('payment_method') || 'Payment Method'}
                >
                  {paymentMethodsFromStore.length > 0 ? (
                    paymentMethodsFromStore.map((pm) => (
                      <MenuItem key={pm.id} value={pm.id}>
                        {pm.name?.toUpperCase() || pm.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      {t('no_payment_methods') || 'No Payment Methods'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Paid Amount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('paid_amount') || 'Paid Amount'}
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* VAT Percentage */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('vat_percentage') || 'VAT Percentage'}</InputLabel>
                <Select
                  value={vatPercentage}
                  onChange={(e) => setVatPercentage(e.target.value)}
                  label={t('vat_percentage') || 'VAT Percentage'}
                >
                  {vatRates && vatRates.length > 0 ? (
                    vatRates.map((rate) => (
                      <MenuItem key={rate.Id || rate.id} value={rate.VATPercentage }>
                         {rate.VATPercentage }% 
                      </MenuItem>
                    ))
                  ) : (
                    [
                      <MenuItem key="default-21" value={21}>Standard - 21%</MenuItem>,
                      <MenuItem key="default-6" value={6}>Reduced - 6%</MenuItem>,
                      <MenuItem key="default-0" value={0}>Zero - 0%</MenuItem>
                    ]
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* 3.2 - Discount Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            p: 2,
            bgcolor: '#fff9e6',
            borderRadius: 2,
            border: '2px solid #ffe082'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.dark' }}>
              🏷️ {t('discount') || 'Discount'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 1 }}>
            {t('discount_description') || 'Apply a discount to this ticket'}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('discount_type') || 'Discount Type'}</InputLabel>
                <Select
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  label={t('discount_type') || 'Discount Type'}
                >
                  <MenuItem value="">
                    <em>{t('no_discount') || 'No Discount'}</em>
                  </MenuItem>
                  <MenuItem value="Percentage">Percentage</MenuItem>
                  <MenuItem value="Fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {discountType && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={discountType === 'Percentage' ? t('discount_percentage') || 'Percentage' : t('discount_amount') || 'Amount'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    InputProps={{
                      startAdornment: discountType === 'Fixed' && <InputAdornment position="start">€</InputAdornment>,
                      endAdornment: discountType === 'Percentage' && <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: discountType === 'Percentage' ? 1 : 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('discount_reason') || 'Discount Reason'}
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder={t('optional') || 'Optional'}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* 3.3 - Additional Notes */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            p: 2,
            bgcolor: '#fce4ec',
            borderRadius: 2,
            border: '2px solid #f8bbd0'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#c2185b' }}>
              📝 {t('additional_notes') || 'Additional Notes'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 1 }}>
            {t('notes_section_description') || 'Add any additional information or instructions'}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('notes') || 'Notes'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('enter_notes_placeholder') || 'Enter notes for this ticket...'}
            helperText={t('notes_helper') || 'Add any additional information about this ticket'}
          />
        </Box>
      </Paper>

      {/* SECTION 4: Original & New Totals - Side by Side */}
      <Grid container spacing={3}>
        {/* Original Ticket Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ReceiptIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('original_ticket_summary') || 'Original Ticket Summary'}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {formatCurrency(ticket.Subtotal)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('vat_amount') || 'VAT Amount'} ({ticket.VATPercentage || 21}%)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {formatCurrency(ticket.VATAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('total_items') || 'Total Items'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {(ticket.LineItems || ticket.Items || []).length}
                </Typography>
              </Box>
              {ticket.DiscountAmount > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t('discount') || 'Discount'} 
                    {ticket.DiscountType === 'Percentage' && ` (${ticket.DiscountPercentage}%)`}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main', mt: 0.5 }}>
                    -{formatCurrency(ticket.DiscountAmount)}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box sx={{ 
                bgcolor: 'grey.200', 
                p: 2, 
                borderRadius: 1,
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('total_amount') || 'Total Amount'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                  {formatCurrency(ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmountInclVAT)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Live Preview - New Totals */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 2, 
            bgcolor: '#e3f2fd', 
            border: '2px solid', 
            borderColor: 'primary.main',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                ⚡ {t('new_totals_preview') || 'New Totals (Preview)'}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2, borderColor: 'primary.light' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 0.5 }}>
                  {formatCurrency(liveTotals.subtotal)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('vat_amount') || 'VAT Amount'} ({vatPercentage}%)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 0.5 }}>
                  {formatCurrency(liveTotals.vatAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('total_items') || 'Total Items'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 0.5 }}>
                  {lineItems.length}
                </Typography>
              </Box>
              {liveTotals.discAmount > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t('discount') || 'Discount'}
                    {discountType === 'Percentage' && ` (${discountValue}%)`}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main', mt: 0.5 }}>
                    -{formatCurrency(liveTotals.discAmount)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ borderColor: 'primary.light' }} />
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 1, 
                border: '2px solid', 
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('total_amount') || 'Total Amount'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                  {formatCurrency(liveTotals.totalAmountInclVATAfterDiscount)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TicketEditPage;
